import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

export const LANGUAGE_COOKIE_NAME = "beesql_language";
export const DEFAULT_LANGUAGE = "en";
export const FR_CA_LANGUAGE = "fr-CA";

const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365 * 50;

const normalizeLanguage = (language) => (language === FR_CA_LANGUAGE || language === "fr" ? FR_CA_LANGUAGE : DEFAULT_LANGUAGE);

export const getLanguageCookie = () => {
  if (typeof document === "undefined") return DEFAULT_LANGUAGE;

  const match = document.cookie
    .split(";")
    .map((cookie) => cookie.trim())
    .find((cookie) => cookie.startsWith(`${LANGUAGE_COOKIE_NAME}=`));

  return normalizeLanguage(match ? decodeURIComponent(match.split("=")[1]) : DEFAULT_LANGUAGE);
};

export const setLanguageCookie = (language) => {
  if (typeof document === "undefined") return;

  document.cookie = `${LANGUAGE_COOKIE_NAME}=${encodeURIComponent(normalizeLanguage(language))}; max-age=${COOKIE_MAX_AGE_SECONDS}; path=/; SameSite=Lax`;
};

const frCA = {
  "Language": "Langue",
  "Language selector": "Sélecteur de langue",
  "English": "Anglais",
  "French (Canada)": "Français (Canada)",
  "Integration Settings": "Paramètres d'intégration",
  "Dashboard": "Tableau de bord",
  "Settings": "Paramètres",
  "Open Settings": "Ouvrir les paramètres",
  "Save": "Enregistrer",
  "Saving...": "Enregistrement...",
  "Saved": "Enregistré",
  "Cancel": "Annuler",
  "Close": "Fermer",
  "Description:": "Description :",
  "Description": "Description",
  "Loading settings...": "Chargement des paramètres...",
  "Failed to load settings": "Échec du chargement des paramètres",
  "Save failed": "Échec de l'enregistrement",
  "Visual": "Visuel",
  "Affect Data Posting": "Affecte la comptabilisation des données",
  "Affecting data posting": "Affecte la comptabilisation des données",
  "Affecting Mapping": "Affecte le mappage",
  "General": "Général",
  "Meal period": "Période de repas",
  "Discount": "Rabais",
  "Gross Sales by Categories (before discount)": "Ventes brutes par catégorie (avant rabais)",
  "By type": "Par type",
  "By category": "Par catégorie",
  "Summary": "Résumé",
  "Tax Option": "Option de taxe",
  "Show COGS": "Afficher le CMV",
  "Calculation field": "Champ de calcul",
  "Credit Card Fees": "Frais de carte de crédit",
  "Surcharge": "Supplément",
  "Off": "Désactivé",
  "On": "Activé",
  "off": "désactivé",
  "on": "activé",
  "Show as Debit / Credit": "Afficher comme débit / crédit",
  "Show as Deducted from Payment | Debit (summary)": "Afficher comme déduit du paiement | Débit (résumé)",
  "Payment without description": "Paiement sans description",
  "Payment without description (One line)": "Paiement sans description (une ligne)",
  "Category without description": "Catégorie sans description",
  "Category without description (One line)": "Catégorie sans description (une ligne)",
  "Show sum of rounds as credit": "Afficher la somme des arrondis comme crédit",
  "Cash Over/Short": "Excédent / déficit de caisse",
  "Time Zone": "Fuseau horaire",
  "Email List": "Liste de courriels",
  "Time": "Heure",
  "GL Id": "ID GL",
  "GL Description": "Description GL",
  "Choose how discounts are grouped: by type, by category, as summary, or disabled completely.": "Choisissez comment les rabais sont regroupés : par type, par catégorie, en résumé ou complètement désactivés.",
  "Enable or disable showing Cost of Goods Sold in reports.": "Activez ou désactivez l'affichage du coût des marchandises vendues dans les rapports.",
  "Choose how credit card fees appear in journals: off, as separate debit and credit lines, or deducted from payment with a summary debit.": "Choisissez comment les frais de carte de crédit apparaissent dans les journaux : désactivés, en lignes débit et crédit séparées, ou déduits du paiement avec un débit résumé.",
  "When enabled, the report will not display the payment name.": "Lorsque cette option est activée, le rapport n'affiche pas le nom du paiement.",
  "When enabled, the report will not display the accounting group name.": "Lorsque cette option est activée, le rapport n'affiche pas le nom du groupe comptable.",
  "If enabled, the sum of rounds is shown as a credit line. Map the GL Account and GL Description used for that credit line.": "Lorsque cette option est activée, la somme des arrondis est affichée comme ligne de crédit. Mappez le compte GL et la description GL utilisés pour cette ligne de crédit.",
  "Choose which Restaurant (K) mode is active: Business Location, Configuration, Order Profile, or Configuration Reporting Tags. Each option uses a separate mapping, and mappings are applied based on the selected configuration.": "Choisissez le mode actif pour Restaurant (K) : établissement, configuration, profil de commande ou balises de rapport de configuration. Chaque option utilise un mappage distinct, appliqué selon la configuration sélectionnée.",
  "Enable additional Restaurant (K) reporting fields such as Order Profile, Order Tag, Group Statistic, and Item Statistic. These settings work only when the Business Location option is enabled.": "Activez des champs de rapport supplémentaires pour Restaurant (K), comme le profil de commande, la balise de commande, la statistique de groupe et la statistique d'article. Ces paramètres fonctionnent uniquement lorsque l'option Établissement est activée.",
  "Choose how credit card fees appear in Restaurant (K) journals: off, as separate debit and credit lines, or deducted from payment with a summary debit.": "Choisissez comment les frais de carte de crédit apparaissent dans les journaux Restaurant (K) : désactivés, en lignes débit et crédit séparées, ou déduits du paiement avec un débit résumé.",
  "Help": "Aide",
  "Search": "Rechercher",
  "Account Description": "Description du compte",
  "Account ID": "ID du compte",
  "Over/Short Description": "Description excédent/déficit",
  "Confirm Save": "Confirmer l'enregistrement",
  "Save report settings?": "Enregistrer les paramètres du rapport?",
  "Save current report settings": "Enregistrer les paramètres actuels du rapport",
  "Save settings": "Enregistrer les paramètres",
  "Settings saved": "Paramètres enregistrés",
  "Automatic Posting Settings": "Paramètres de comptabilisation automatique",
  "Loading automatic posting settings...": "Chargement des paramètres de comptabilisation automatique...",
  "Import status notification email is required.": "Le courriel de notification du statut d'importation est requis.",
  "Add new location": "Ajouter un nouvel emplacement",
  "Business Location": "Établissement",
  "Configuration": "Configuration",
  "Order Profile": "Profil de commande",
  "Configuration Reporting Tags": "Balises de rapport de configuration",
  "Payments": "Paiements",
  "Payments deferred": "Paiements différés",
  "Categories": "Catégories",
  "Categories Deferred": "Catégories différées",
  "Categories Multi GL": "Catégories multi-GL",
  "Categories Round": "Catégories - arrondis",
  "Other": "Autre",
  "Items without category": "Articles sans catégorie",
  "Taxes": "Taxes",
  "Report Retail": "Rapport Retail",
  "Imports (R)": "Imports (R)",
  "Imports (PO)": "Imports (PO)",
  "Imports (X)": "Imports (X)",
  "Action": "Action",
  "Actions": "Actions",
  "Select all": "Tout sélectionner",
  "Select all visible & filtered": "Tout sélectionner visible et filtré",
  "Clear": "Effacer",
  "Clear selection": "Effacer la sélection",
  "Selected:": "Sélectionné :",
  "Selected location": "Emplacement sélectionné",
  "Selected shop": "Boutique sélectionnée",
  "Location ID": "ID d'emplacement",
  "From:": "Du :",
  "To:": "Au :",
  "Date": "Date",
  "Amount": "Montant",
  "Status": "Statut",
  "Visual & Display": "Affichage visuel",
  "Report display options": "Options d'affichage du rapport",
  "Sales & Discounts": "Ventes et rabais",
  "Sales calculation options": "Options de calcul des ventes",
  "Tax posting options": "Options de comptabilisation des taxes",
  "Payment and fee options": "Options de paiement et de frais",
  "Regional Settings": "Paramètres régionaux",
  "Time zone settings": "Paramètres de fuseau horaire",
  "Automation & Delivery": "Automatisation et livraison",
  "Email delivery settings": "Paramètres d'envoi par courriel",
  "Data Posting": "Comptabilisation des données",
  "Posting options": "Options de comptabilisation",
  "Mapping": "Mappage",
  "Restaurant (K) mapping mode": "Mode de mappage Restaurant (K)",
  "deferred": "différés",
  "Deferred": "Différées",
  "Multi GL": "multi-GL",
  "Round": "Arrondis",
  "Items without": "Articles sans",
  "category": "catégorie",
  "Payment Mapping": "Mappage des paiements",
  "Category Mapping": "Mappage des catégories",  "Posting settings": "Paramètres de comptabilisation",
  "Automatic posting": "Comptabilisation automatique",
  "Enabled": "Activé",
  "Disabled": "Désactivé",
  "Automatically imports transactions for the selected day at the selected time.": "Importe automatiquement les transactions du jour sélectionné à l'heure sélectionnée.",
  "Import status notification email (for multiple emails use ,)": "Courriel de notification du statut d'importation (pour plusieurs courriels, utilisez ,)",

  "Extra visibility": "Visibilité supplémentaire",
  "Show Order Profile": "Afficher le profil de commande",
  "Show Order tag": "Afficher la balise de commande",
  "Group Statistic": "Statistique de groupe",
  "Item Statistic": "Statistique d'article",
};

const dictionaries = {
  [FR_CA_LANGUAGE]: frCA,
};

const LanguageContext = createContext({
  language: DEFAULT_LANGUAGE,
  setLanguage: () => {},
  t: (text) => text,
});

const getTextTranslation = (language, text) => {
  const dictionary = dictionaries[language];
  if (!dictionary || typeof text !== "string") return text;

  const trimmed = text.trim();
  if (!trimmed) return text;

  const translated = dictionary[trimmed];
  if (!translated) return text;

  return text.replace(trimmed, translated);
};

const textNodeOriginals = new WeakMap();
const translatedAttributeName = "data-beesql-i18n-original";
const translatedAttributes = ["title", "aria-label", "placeholder", "alt"];

const shouldSkipSubtree = (node) => {
  const element = node.nodeType === Node.ELEMENT_NODE ? node : node.parentElement;
  return Boolean(element?.closest("script, style, textarea, select, option, [data-no-translate], .no-translate"));
};

const shouldSkipTextNode = (node) =>
  Boolean(node.parentElement?.closest("script, style, textarea, input, select, option, [data-no-translate], .no-translate"));

const translateElementAttributes = (element, language) => {
  translatedAttributes.forEach((attributeName) => {
    if (!element.hasAttribute(attributeName)) return;

    const storeName = `${translatedAttributeName}-${attributeName}`;
    const originalValue = element.getAttribute(storeName) || element.getAttribute(attributeName);

    if (!element.hasAttribute(storeName)) {
      element.setAttribute(storeName, originalValue);
    }

    const nextValue = getTextTranslation(language, originalValue);
    if (element.getAttribute(attributeName) !== nextValue) {
      element.setAttribute(attributeName, nextValue);
    }
  });
};

const translateNode = (node, language) => {
  if (node.nodeType === Node.TEXT_NODE) {
    if (shouldSkipTextNode(node)) return;

    const originalValue = textNodeOriginals.get(node) || node.nodeValue;
    if (!textNodeOriginals.has(node)) {
      textNodeOriginals.set(node, originalValue);
    }

    const translatedValue = getTextTranslation(language, originalValue);
    if (node.nodeValue !== translatedValue) node.nodeValue = translatedValue;
    return;
  }

  if (node.nodeType !== Node.ELEMENT_NODE || shouldSkipSubtree(node)) return;

  translateElementAttributes(node, language);
  node.childNodes.forEach((child) => translateNode(child, language));
};

const translateDocument = (language) => {
  if (typeof document === "undefined" || !document.body) return;
  translateNode(document.body, normalizeLanguage(language));
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguageState] = useState(getLanguageCookie);

  const setLanguage = useCallback((nextLanguage) => {
    const normalizedLanguage = normalizeLanguage(nextLanguage);
    setLanguageCookie(normalizedLanguage);
    setLanguageState(normalizedLanguage);
  }, []);

  const t = useCallback((text) => getTextTranslation(language, text), [language]);

  useEffect(() => {
    translateDocument(language);

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => translateNode(node, language));
        if (mutation.type === "attributes") translateNode(mutation.target, language);
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: translatedAttributes,
    });

    return () => observer.disconnect();
  }, [language]);

  const value = useMemo(() => ({ language, setLanguage, t }), [language, setLanguage, t]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useI18n = () => useContext(LanguageContext);
